import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  createFormationApi,
  deleteFormationApi,
  fetchFormationById,
  fetchFormations,
  updateFormationApi,
  validateFormationApi,
} from "../services/formationsService";
import { resolveApiErrorMessage } from "../utils/resolveApiErrorMessage";

const FORMATIONS_QUERY_KEY = ["formations"];

export const useFormations = (params = {}) =>
  useQuery({
    queryKey: [...FORMATIONS_QUERY_KEY, params],
    queryFn: () => fetchFormations(params),
  });

export const useFormation = (id, options = {}) =>
  useQuery({
    queryKey: [...FORMATIONS_QUERY_KEY, id],
    queryFn: () => fetchFormationById(id),
    enabled: !!id,
    ...options,
  });

export const useCreateFormation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createFormationApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FORMATIONS_QUERY_KEY });
      toast.success("Formation bien ajoutée", { autoClose: 3000 });
    },
    onError: (err) => {
      const msg = resolveApiErrorMessage(err, "Formation non ajoutée");
      toast.error(msg, { autoClose: 3000 });
    },
  });
};

export const useUpdateFormation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => updateFormationApi(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: FORMATIONS_QUERY_KEY });
      if (variables?.id) {
        queryClient.invalidateQueries({
          queryKey: [...FORMATIONS_QUERY_KEY, variables.id],
        });
      }
      toast.success("Formation bien modifiée", { autoClose: 3000 });
    },
    onError: (err) => {
      const msg = resolveApiErrorMessage(err, "Formation non modifiée");
      toast.error(msg, { autoClose: 3000 });
    },
  });
};

export const useValidateFormation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => validateFormationApi(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: FORMATIONS_QUERY_KEY });
      if (id) {
        queryClient.invalidateQueries({
          queryKey: [...FORMATIONS_QUERY_KEY, id],
        });
      }
      toast.success("Formation validée avec succès", { autoClose: 3000 });
    },
    onError: (err) => {
      const msg = resolveApiErrorMessage(err, "Formation non validée");
      toast.error(msg, { autoClose: 3000 });
    },
  });
};

export const useDeleteFormation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteFormationApi(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FORMATIONS_QUERY_KEY });
      toast.success("Formation supprimée avec succès", { autoClose: 3000 });
    },
    onError: (err) => {
      const msg = resolveApiErrorMessage(
        err,
        "Une erreur est survenue lors de la suppression"
      );
      toast.error(msg, { autoClose: 3000 });
    },
  });
};
